import React, { useId, useRef, useState, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
} from '@tanstack/react-table';

import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import type { TaskSummary } from '../../api';
import { PartnerTaskStatusFilter } from '../../api';
import { DRUG_CATEGORY_DROPDOWN_OPTIONS } from '../../constants';
import {
  sortOfficeNames,
  stringsToDropdownOptions,
  getDropdownInitialValueFromSearchParams,
  getInitialMultiselectFilterValueFromCookie,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
  formatStandardDateLocaleString,
} from '../../utils';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import { TableMultiselectFilter } from '../TableDropdownFilter/TableDropdownFilter';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from '../SaveFiltersButton';

import { areStringArraysEqual } from '../../utils';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { AuthContext } from '../../auth';

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  FilterBarRow,
  SearchInput,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  CenteredTableCell,
  TableTabList,
  ResultCountContainer,
  ResultCount,
  FilterBarRowSection,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
} from '../Table';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import FormattedCell from '../FormattedCell';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';

const columnHelper = createColumnHelper<TaskSummary>();

interface TaskTableProps {
  data: TaskSummary[];
  isLoading?: boolean;
  error?: Error | null;
}

const taskDateSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const taskPatientSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string };
  const bValue = b.getValue(columnId) as { name: string; dob: string };
  return bValue.name.localeCompare(aValue.name);
};

const taskTypeSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  const aValue = a.getValue(columnId) as { type: string; drugDescription: string; message: string };
  const bValue = b.getValue(columnId) as { type: string; drugDescription: string; message: string };
  return bValue.type.localeCompare(aValue.type);
};

const taskTableMultiSelectFilter = (row: Row<TaskSummary>, columnId: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

// drug info columns need a special filter function because they're in a nested
// object
const drugInfoMultiSelectFilter = (
  row: Row<TaskSummary>,
  columnId: 'prescriber_name' | 'drug_category',
  filterValue: string[],
) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  return filterValue.includes(row.original.drug?.[columnId] || '');
};

const getDefaultNullStateType = (status: PartnerTaskStatusFilter) => {
  if (status === PartnerTaskStatusFilter.SENT) {
    return TableNullStateType.NO_TASKS_SENT;
  } else if (status === PartnerTaskStatusFilter.COMPLETED) {
    return TableNullStateType.NO_TASKS_COMPLETED;
  } else {
    return TableNullStateType.NO_TASKS_OPEN;
  }
};

const TasksTable: React.FC<TaskTableProps> = ({ data, isLoading = false, error = null }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const tasksTabId = useId();
  const tasksPanelId = useId();

  const { data: filterOptions } = useFetchPortalTableFilters();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState('');

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const typeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugCategoryFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    {
      id: 'office_name',
      value: getInitialTableColumnFilterValueFromCookie('tasks', 'office_name', userEmail),
    },
    {
      id: 'prescriber_name',
      value: defaultPrescriberNames || [],
    },
  ]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const typeStrings: string[] = [];
  data.forEach((task) => {
    if (task.task_type) {
      typeStrings.push(task.task_type);
    }
  });

  const columns = [
    columnHelper.accessor('last_comment_created_at', {
      header: 'Last updated',
      sortingFn: taskDateSortingFn,
      size: DATE_COLUMN_SIZE,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: taskPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${info.getValue().dob}`}
            size={DEFAULT_COLUMN_SIZE}
          />
        ),
      },
    ),
    columnHelper.accessor(
      (row) => {
        return { type: row.task_type, message: row.last_comment_message, drugDescription: row.drug?.drug_name };
      },
      {
        id: 'task',
        header: 'Task',
        sortingFn: taskTypeSortingFn,
        cell: (info) => {
          const attachmentsCount = info.row.original.last_comment_attachments_count;
          const attachmentText = attachmentsCount
            ? ` [uploaded ${attachmentsCount} document${attachmentsCount > 1 ? 's' : ''}]`
            : '';
          const messageText = `${info.getValue().message?.replaceAll(/<[^>]*>/g, '') || ''}${attachmentText}`;
          return (
            <FormattedCell
              id={info.row.original.id}
              title={info.getValue().type}
              subtitle={info.getValue().drugDescription}
              body={messageText}
              truncateBody
            />
          );
        },
        filterFn: (row, _, filterValue) => {
          if (!filterValue.length) return true;
          return filterValue.includes(row.original.task_type);
        },
        size: 600,
      },
    ),
    columnHelper.accessor((row) => row.drug?.prescriber_name || '', {
      id: 'prescriber_name',
      header: 'Prescriber',
      filterFn: (row, _, filterValue) => drugInfoMultiSelectFilter(row, 'prescriber_name', filterValue),
      size: DEFAULT_COLUMN_SIZE,
    }),

    // these columns are only used for filtering, not displaying
    columnHelper.accessor('task_type', {
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
    columnHelper.accessor((row) => row.drug?.drug_category || '', {
      id: 'drug_category',
      header: 'Drug category',
      filterFn: (row, _, filterValue) => drugInfoMultiSelectFilter(row, 'drug_category', filterValue),
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
    // Explicitly define the global filter function so that it works despite the formatted cells
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;

      const searchTerm = filterValue.toLowerCase();

      if (row.original.patient_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.drug?.prescriber_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.drug?.drug_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.task_type?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.last_comment_message?.toLowerCase().includes(searchTerm)) return true;

      const lastCommentCreatedAtStr = formatStandardDateLocaleString(row.original.last_comment_created_at);
      if (lastCommentCreatedAtStr.toLowerCase().includes(searchTerm)) return true;

      if (row.original.patient_dob.toLowerCase().includes(searchTerm)) return true;

      return false;
    },
    // these columns are only used for filtering, not displaying
    initialState: { columnVisibility: { drug_category: false, office_name: false, task_type: false } },
  });

  const setStatus = React.useCallback(
    (status: string) => {
      setSearchParams({ status });
    },
    [setSearchParams],
  );

  // Get current status from URL for UI state
  const currentStatus = searchParams.get('status') || PartnerTaskStatusFilter.OPEN;

  // Render table body content based on state
  const renderTableBody = () => {
    if (error) {
      return (
        <tr>
          <TableCell colSpan={columns.length}>
            <PageLoadError />
          </TableCell>
        </tr>
      );
    }

    if (isLoading) {
      return (
        <tr>
          <CenteredTableCell colSpan={columns.length}>
            <LoadingSpinner />
          </CenteredTableCell>
        </tr>
      );
    }

    if (table.getRowModel().rows?.length) {
      return table.getRowModel().rows.map((row) => (
        <ClickableRow
          key={row.original.id}
          onClick={() => navigate(row.original.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate(row.original.id);
            }
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} $width={`${cell.column.getSize()}px`}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </ClickableRow>
      ));
    }

    return (
      <tr>
        <TableCell colSpan={columns.length}>
          <TableNullState nullStateType={nullStateType} onClearFilters={clearAllFilters} />
        </TableCell>
      </tr>
    );
  };

  const nullStateType = useTableNullState({
    columnFilters,
    table,
    filterIds: ['office_name', 'prescriber_name', 'task_type', 'drug_category'],
    defaultNullStateType: getDefaultNullStateType(currentStatus as PartnerTaskStatusFilter),
    globalFilter: searchValue,
  });

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by URL)
    const statusFilter = columnFilters.find((f) => f.id === 'status');
    setColumnFilters(statusFilter ? [statusFilter] : []);

    // Clear global filter and search input
    table.setGlobalFilter('');
    setSearchValue('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], 'tasks', 'office_name', userEmail);

    // Clear TableMultiselectFilter components
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    typeFilterRef.current?.clear();
    drugCategoryFilterRef.current?.clear();

    // TODO: after clearing the filters, we should set the focus state on another element on the page
    // because the focus is currently on the clear filters button, which will disappear after the
    // filters are cleared
  }, [columnFilters, table, userEmail]);

  return (
    <TableContainer>
      <FilterBar>
        <FilterBarRow>
          <TableTabList
            tabs={[
              {
                label: 'Requires response',
                value: 'open',
              },
              {
                label: 'Awaiting Tandem',
                value: 'sent',
              },
              {
                label: 'Closed',
                value: 'completed',
              },
            ]}
            selectedValue={currentStatus}
            onSelect={setStatus}
            tabPanelId={tasksPanelId}
            tabListId={tasksTabId}
            ariaLabel="Filter tasks by status"
          />
        </FilterBarRow>

        <FilterBarRow $reorderOnSmall>
          <SearchInput
            label="Search by patient name or DOB"
            hideLabel
            value={searchValue}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setSearchValue(value);
              table.setGlobalFilter(value);
            }}
          />
          <TableMultiselectFilter
            ref={officeFilterRef}
            options={filterOptions?.offices || []}
            label={'Office'}
            initialValue={getInitialMultiselectFilterValueFromCookie('tasks', 'office_name', userEmail)}
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              table.getColumn('office_name')!.setFilterValue(selectedValuesList);
              updateTableColumnFilterValueCookie(value, 'tasks', 'office_name', userEmail);
            }}
            sortFn={sortOfficeNames}
          />
          <TableMultiselectFilter
            ref={prescriberFilterRef}
            options={filterOptions?.prescribers || []}
            label={'Prescriber'}
            initialValue={
              getDropdownInitialValueFromSearchParams(
                authContext.user?.default_filters,
                '',
                'providers',
                filterOptions?.prescribers,
              ) as MultiselectAutocompleteOption[]
            }
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedNamesList = value.map((valueItem) => valueItem.description);
              table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
            }}
          />
          <TableMultiselectFilter
            ref={typeFilterRef}
            options={stringsToDropdownOptions(typeStrings)}
            label={'Type'}
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              table.getColumn('task_type')!.setFilterValue(selectedValuesList);
            }}
          />
          <TableMultiselectFilter
            ref={drugCategoryFilterRef}
            options={DRUG_CATEGORY_DROPDOWN_OPTIONS}
            label={'Drug category'}
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              table.getColumn('drug_category')!.setFilterValue(selectedValuesList);
            }}
          />
          <SaveFiltersButton $visible={showSaveButton} onClick={() => setShowSaveModal(true)} />

          <FilterBarRowSection>
            <ResultCountContainer>
              <ResultCount>
                {table.getRowModel().rows?.length} {table.getRowModel().rows?.length === 1 ? 'result' : 'results'}
              </ResultCount>
            </ResultCountContainer>
          </FilterBarRowSection>
        </FilterBarRow>
      </FilterBar>

      <Table role="tabpanel" id={tasksPanelId} aria-labelledby={tasksTabId}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    key={header.id}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    $sortable={header.column.getCanSort()}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <HeaderSortingIndicator
                        isSorted={header.column.getIsSorted()}
                        sortDirection={header.column.getIsSorted()}
                      />
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>{renderTableBody()}</tbody>
      </Table>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </TableContainer>
  );
};

export default TasksTable;
